import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';

export interface EnvironmentModel {
  model: THREE.Group;
  textures: Map<string, THREE.Texture>;
}

export class EnvironmentModelManager {
  private loader: FBXLoader;
  private textureLoader: THREE.TextureLoader;
  private modelCache: Map<string, EnvironmentModel> = new Map();

  constructor() {
    this.loader = new FBXLoader();
    this.textureLoader = new THREE.TextureLoader();
  }

  async loadEnvironmentModel(
    modelId: string,
    modelUrl: string,
    textureUrls: string[] = [],
    scale: number = 1,
    position?: { x: number; y: number; z: number }
  ): Promise<EnvironmentModel> {
    // Check cache first
    const cacheKey = `${modelId}_${modelUrl}`;
    if (this.modelCache.has(cacheKey)) {
      return this.modelCache.get(cacheKey)!;
    }

    try {
      console.log(`[EnvironmentModelManager] Loading environment model ${modelId} from ${modelUrl}`);
      
      // Load the main model
      const model = await this.loadFBX(modelUrl);
      
      // Нормализация по высоте как для персонажей
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      
      // Базовое масштабирование по высоте (нормализуем к разумному размеру для окружения)
      // Используем больший размер чем для персонажей, так как это окружение
      const targetHeight = 10; // 10 единиц высоты для окружения (персонажи 1.8)
      const baseScale = targetHeight / size.y;
      const finalScale = baseScale * (scale || 1);
      model.scale.set(finalScale, finalScale, finalScale);
      
      console.log(`[EnvironmentModelManager] Model normalized: original height ${size.y}, target height ${targetHeight}, final scale ${finalScale}`);
      
      // Apply position if provided
      if (position) {
        model.position.set(position.x, position.y, position.z);
        console.log(`[EnvironmentModelManager] Model positioned at: x=${position.x}, y=${position.y}, z=${position.z}`);
      }
      
      // Load textures
      const textures = new Map<string, THREE.Texture>();
      for (const textureUrl of textureUrls) {
        try {
          const texture = await this.loadTexture(textureUrl);
          const textureName = textureUrl.split('/').pop() || textureUrl;
          textures.set(textureName, texture);
          console.log(`[EnvironmentModelManager] Loaded texture: ${textureName}`);
        } catch (error) {
          console.warn(`[EnvironmentModelManager] Failed to load texture: ${textureUrl}`, error);
        }
      }

      // Apply first texture to all materials if available
      if (textures.size > 0) {
        const firstTexture = textures.values().next().value;
        if (firstTexture) {
          this.applyTextureToModel(model, firstTexture);
        }
      }

      // Enable shadows
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          
          // Make materials double-sided
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                mat.side = THREE.DoubleSide;
              });
            } else {
              child.material.side = THREE.DoubleSide;
            }
          }
        }
      });

      // Setup transparency for materials that need it
      await this.setupTransparency(model);

      const environmentModel: EnvironmentModel = {
        model,
        textures
      };

      // Cache the model
      this.modelCache.set(cacheKey, environmentModel);

      return environmentModel;
    } catch (error) {
      console.error(`[EnvironmentModelManager] Failed to load environment model ${modelId}:`, error);
      throw error;
    }
  }

  private loadFBX(path: string): Promise<THREE.Group> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        path,
        (object) => {
          console.log(`[EnvironmentModelManager] FBX loaded successfully`);
          resolve(object);
        },
        (progress) => {
          const percent = (progress.loaded / progress.total) * 100;
          console.log(`[EnvironmentModelManager] Loading model: ${percent.toFixed(2)}%`);
        },
        (error) => {
          console.error(`[EnvironmentModelManager] Error loading FBX:`, error);
          reject(error);
        }
      );
    });
  }

  private loadTexture(path: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        path,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          resolve(texture);
        },
        undefined,
        (error) => reject(error)
      );
    });
  }

  private applyTextureToModel(model: THREE.Group, texture: THREE.Texture) {
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.material instanceof THREE.Material) {
          const material = child.material as THREE.MeshStandardMaterial;
          material.map = texture;
          material.needsUpdate = true;
        } else if (Array.isArray(child.material)) {
          child.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.map = texture;
              mat.needsUpdate = true;
            }
          });
        }
      }
    });
  }

  private async setupTransparency(model: THREE.Group) {
    let transparentMaterialsCount = 0;
    const materialsToCheck: Array<{ material: THREE.Material, texture: THREE.Texture }> = [];
    
    // Первый проход - собираем материалы с текстурами
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const collectMaterials = (material: THREE.Material) => {
          // Собираем материалы с текстурами для проверки
          if (material instanceof THREE.MeshStandardMaterial || 
              material instanceof THREE.MeshBasicMaterial ||
              material instanceof THREE.MeshPhongMaterial) {
            
            const texture = material.map || material.alphaMap;
            if (texture && !material.transparent) {
              materialsToCheck.push({ material, texture });
            }
          }
        };
        
        // Обрабатываем материалы
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(collectMaterials);
          } else {
            collectMaterials(child.material);
          }
        }
      }
    });

    // Второй проход - проверяем текстуры на альфа-канал
    const checkPromises = materialsToCheck.map(async ({ material, texture }) => {
      const materialName = material.name || 'unnamed';
      const hasAlpha = await this.checkTextureAlpha(texture, materialName);
      
      if (hasAlpha) {
        // Включаем прозрачность только для текстур с альфа-каналом
        material.transparent = true;
        material.alphaTest = 0.5; // Отсекаем пиксели с альфой < 0.5
        material.depthWrite = true; // Оставляем включенным для правильного порядка отрисовки!
        material.needsUpdate = true;
        
        transparentMaterialsCount++;
        console.log(`[EnvironmentModelManager] Enabled transparency for material: ${materialName} (has alpha channel)`);
      } else {
        console.log(`[EnvironmentModelManager] Material ${materialName} has no alpha channel, keeping opaque`);
      }
    });

    // Ждем завершения всех проверок
    await Promise.all(checkPromises);
    
    if (transparentMaterialsCount > 0) {
      console.log(`[EnvironmentModelManager] Total materials with transparency enabled: ${transparentMaterialsCount}`);
    } else {
      console.log(`[EnvironmentModelManager] No materials with alpha channel found`);
    }
  }

  // Функция для проверки наличия альфа-канала в текстуре
  private checkTextureAlpha(texture: THREE.Texture, materialName: string): Promise<boolean> {
    return new Promise((resolve) => {
      const checkImage = () => {
        console.log(`[EnvironmentModelManager] Checking texture for material: ${materialName}`);
        console.log(`[EnvironmentModelManager] Texture object:`, texture);
        console.log(`[EnvironmentModelManager] Texture image:`, texture.image);
        
        if (!texture.image) {
          console.warn(`[EnvironmentModelManager] Texture image not loaded for material: ${materialName}`);
          resolve(false);
          return;
        }

        try {
          // Создаем canvas для анализа
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            console.error('[EnvironmentModelManager] Failed to get canvas context');
            resolve(false);
            return;
          }

          // Устанавливаем размер canvas
          const width = Math.min(texture.image.width, 256); // Ограничиваем размер для производительности
          const height = Math.min(texture.image.height, 256);
          canvas.width = width;
          canvas.height = height;

          console.log(`[EnvironmentModelManager] Analyzing texture ${width}x${height} for material: ${materialName}`);

          // Рисуем текстуру на canvas
          ctx.drawImage(texture.image, 0, 0, width, height);

          // Получаем данные пикселей
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;

          let hasAlpha = false;
          let alphaPixelCount = 0;

          // Проверяем каждый 4-й пиксель (для производительности)
          for (let i = 3; i < data.length; i += 16) { // i += 16 = каждый 4-й пиксель
            if (data[i] < 255) { // Альфа-канал < 255
              hasAlpha = true;
              alphaPixelCount++;
            }
          }

          if (hasAlpha) {
            console.log(`[EnvironmentModelManager] Material ${materialName} has alpha channel (${alphaPixelCount} transparent pixels found)`);
          } else {
            console.log(`[EnvironmentModelManager] Material ${materialName} has no alpha channel`);
          }
          
          resolve(hasAlpha);
        } catch (error) {
          console.error(`[EnvironmentModelManager] Error checking texture alpha for ${materialName}:`, error);
          resolve(false);
        }
      };

      // Проверяем текстуру с задержкой, чтобы дать время на загрузку
      setTimeout(checkImage, 500); // Увеличил задержку до 500мс
    });
  }

  getModel(modelId: string): EnvironmentModel | undefined {
    const entries = Array.from(this.modelCache.entries());
    for (const [key, model] of entries) {
      if (key.startsWith(modelId)) {
        return model;
      }
    }
    return undefined;
  }

  dispose() {
    // Clean up cached models
    this.modelCache.forEach((envModel) => {
      // Dispose textures
      envModel.textures.forEach((texture) => {
        texture.dispose();
      });

      // Dispose model
      envModel.model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          } else if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose());
          }
        }
      });
    });
    this.modelCache.clear();
  }
}
